import React, { createContext, FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import StaffMemberT, { PrimarySpecialty } from '@/types/StaffMember';
import StaffShiftT from '@/types/StaffShift';
import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import {
  addStaffShiftProcedure,
  addStaffShiftRoom,
  listForShift,
  listForShiftByPrimarySpecialty,
  removeStaffShiftProcedure,
  removeStaffShiftRoom,
} from '@/graph/staff';
import { useApolloClient, useMutation } from '@apollo/client';

export type StaffShiftScope = StaffShiftProcedureScope | StaffShiftRoomScope;

interface StaffShiftProcedureScope {
  procedureId: number;
  date: string;
}

interface StaffShiftRoomScope {
  roomId: number;
  date: string;
}

interface StaffShiftContextData {
  scope: StaffShiftScope;
  staffShifts: StaffShiftT[];
  staffMembers: {
    list: TypedDocumentNode<{ staffMembers: StaffMemberT[] }, StaffShiftScope>;
    listByPrimarySpecialty: TypedDocumentNode<{ staffMembers: StaffMemberT[] }, StaffShiftScope>;
    variables: StaffShiftScope;
  };
  add: (staffId?: number, hourlyRate?: number) => Promise<void>;
  remove: (id: number, shiftHospitalId: number, staffHospitalId: number) => Promise<void>;
}

const StaffShiftContext = createContext<StaffShiftContextData>({
  scope: { procedureId: 0, date: '' },
  staffShifts: [],
  staffMembers: {} as any,
  add: async () => {},
  remove: async () => {},
});

export function useStaffShiftContext() {
  return useContext(StaffShiftContext);
}

interface StaffMembersSubscriptionContextValue {
  staffMembers: StaffMemberT[] | null;
  loading: boolean;
  error?: Error;
  activateSubscription: (options?: { primarySpecialty?: PrimarySpecialty | null | undefined }) => void;
  setPrimarySpecialty: (primarySpecialty: PrimarySpecialty | null | undefined) => void;
  primarySpecialty: PrimarySpecialty | null | undefined;
}

const DEFAULT_CACHE_KEY = '__ALL__';
const NULL_CACHE_KEY = '__NULL__';
const DEFAULT_MAX_CACHE_ENTRIES = 20;

const formatScopeSegment = (value: number | null | undefined, label: string) =>
  value !== null && value !== undefined ? `${label}:${value}` : `${label}:__none__`;

const getCacheKey = (
  primarySpecialty: PrimarySpecialty | null | undefined,
  variables: StaffShiftContextData['staffMembers']['variables']
) => {
  const roomId = 'roomId' in variables ? variables.roomId : undefined;
  const procedureId = 'procedureId' in variables ? variables.procedureId : undefined;
  const roomSegment = formatScopeSegment(roomId, 'room');
  const procedureSegment = formatScopeSegment(procedureId, 'procedure');

  const primarySegment =
    primarySpecialty === undefined ? DEFAULT_CACHE_KEY : primarySpecialty === null ? NULL_CACHE_KEY : primarySpecialty;

  return `${roomSegment}|${procedureSegment}|primary:${primarySegment}`;
};

const buildSubscriptionConfig = (
  staffMembersConfig: StaffShiftContextData['staffMembers'],
  primarySpecialty: PrimarySpecialty | null | undefined
) => {
  if (primarySpecialty === undefined) {
    return {
      document: staffMembersConfig.list,
      variables: staffMembersConfig.variables,
    };
  }

  return {
    document: staffMembersConfig.listByPrimarySpecialty,
    variables: {
      ...staffMembersConfig.variables,
      primarySpecialty,
    },
  };
};

interface StaffMembersSubscriptionEntry {
  subscription: { unsubscribe: () => void } | null;
  data: StaffMemberT[];
  loading: boolean;
  hasLoaded: boolean;
  error?: Error;
  lastAccessed: number;
}

const StaffMembersSubscriptionContext = createContext<StaffMembersSubscriptionContextValue | null>(null);

export function useStaffMembersSubscription(): StaffMembersSubscriptionContextValue {
  const context = useContext(StaffMembersSubscriptionContext);

  if (!context) {
    throw new Error('useStaffMembersSubscription must be used within StaffMembersSubscriptionProvider');
  }

  return context;
}

interface StaffMembersSubscriptionProviderProps {
  staffMembersConfig: StaffShiftContextData['staffMembers'];
  scopeKey: string;
  children: React.ReactNode;
  maxCacheEntries?: number;
}

const StaffMembersSubscriptionProvider: FC<StaffMembersSubscriptionProviderProps> = ({
  staffMembersConfig,
  scopeKey,
  maxCacheEntries = DEFAULT_MAX_CACHE_ENTRIES,
  children,
}) => {
  const client = useApolloClient();
  const subscriptionsRef = useRef<Record<string, StaffMembersSubscriptionEntry>>({});
  const [primarySpecialty, setPrimarySpecialtyState] = useState<PrimarySpecialty | null | undefined>(undefined);
  const [, forceUpdate] = useState(0);

  const bump = useCallback(() => {
    forceUpdate(value => value + 1);
  }, []);

  const touchEntry = useCallback((cacheKey: string) => {
    const entry = subscriptionsRef.current[cacheKey];
    if (entry) {
      entry.lastAccessed = Date.now();
    }
  }, []);

  const evictIfNeeded = useCallback(() => {
    const keys = Object.keys(subscriptionsRef.current);

    if (keys.length <= maxCacheEntries) {
      return;
    }

    const sortedKeys = keys.sort((a, b) => {
      const aEntry = subscriptionsRef.current[a];
      const bEntry = subscriptionsRef.current[b];
      return (aEntry?.lastAccessed ?? 0) - (bEntry?.lastAccessed ?? 0);
    });

    while (sortedKeys.length > maxCacheEntries) {
      const keyToRemove = sortedKeys.shift();
      if (!keyToRemove) {
        break;
      }
      const entry = subscriptionsRef.current[keyToRemove];
      entry?.subscription?.unsubscribe();
      delete subscriptionsRef.current[keyToRemove];
    }

    bump();
  }, [bump, maxCacheEntries]);

  const cleanupSubscriptions = useCallback(() => {
    Object.values(subscriptionsRef.current).forEach(entry => {
      entry.subscription?.unsubscribe();
    });
    subscriptionsRef.current = {};
  }, []);

  useEffect(
    () => () => {
      cleanupSubscriptions();
    },
    [cleanupSubscriptions]
  );

  useEffect(() => {
    cleanupSubscriptions();
    setPrimarySpecialtyState(undefined);
    bump();
  }, [scopeKey, cleanupSubscriptions, bump]);

  const ensureSubscription = useCallback(
    (targetPrimarySpecialty: PrimarySpecialty | null | undefined) => {
      const cacheKey = getCacheKey(targetPrimarySpecialty, staffMembersConfig.variables);

      if (subscriptionsRef.current[cacheKey]) {
        touchEntry(cacheKey);
        evictIfNeeded();
        return;
      }

      const { document, variables } = buildSubscriptionConfig(staffMembersConfig, targetPrimarySpecialty);

      const entry: StaffMembersSubscriptionEntry = {
        subscription: null,
        data: [],
        loading: true,
        hasLoaded: false,
        lastAccessed: Date.now(),
      };

      const observable = client.subscribe<{ staffMembers: StaffMemberT[] }>({
        query: document,
        variables: variables as Record<string, unknown>,
      });

      entry.subscription = observable.subscribe({
        next: result => {
          entry.data = (result.data?.staffMembers as StaffMemberT[]) || [];
          entry.loading = false;
          entry.hasLoaded = true;
          bump();
        },
        error: err => {
          entry.error = err instanceof Error ? err : new Error(String(err));
          entry.loading = false;
          entry.hasLoaded = true;
          bump();
        },
      });

      subscriptionsRef.current[cacheKey] = entry;
      touchEntry(cacheKey);
      evictIfNeeded();
      bump();
    },
    [bump, client, evictIfNeeded, staffMembersConfig, touchEntry]
  );

  const updatePrimarySpecialty = useCallback((value: PrimarySpecialty | null | undefined) => {
    setPrimarySpecialtyState(prev => (prev === value ? prev : value));
  }, []);

  const activateSubscription = useCallback(
    (options?: { primarySpecialty?: PrimarySpecialty | null | undefined }) => {
      const hasPrimarySpecialtyOption = options && Object.prototype.hasOwnProperty.call(options, 'primarySpecialty');
      const targetPrimarySpecialty = hasPrimarySpecialtyOption ? options!.primarySpecialty : primarySpecialty;

      if (hasPrimarySpecialtyOption) {
        updatePrimarySpecialty(targetPrimarySpecialty);
      }

      ensureSubscription(targetPrimarySpecialty);
    },
    [ensureSubscription, primarySpecialty, updatePrimarySpecialty]
  );

  const currentCacheKey = getCacheKey(primarySpecialty, staffMembersConfig.variables);
  touchEntry(currentCacheKey);
  evictIfNeeded();
  const currentEntry = subscriptionsRef.current[currentCacheKey];
  const staffMembers = currentEntry?.hasLoaded ? currentEntry.data : null;
  const loading = currentEntry?.loading ?? false;
  const error = currentEntry?.error;

  const contextValue = useMemo<StaffMembersSubscriptionContextValue>(
    () => ({
      staffMembers,
      loading,
      error,
      activateSubscription,
      setPrimarySpecialty: updatePrimarySpecialty,
      primarySpecialty,
    }),
    [activateSubscription, error, loading, primarySpecialty, staffMembers, updatePrimarySpecialty]
  );

  return (
    <StaffMembersSubscriptionContext.Provider value={contextValue}>{children}</StaffMembersSubscriptionContext.Provider>
  );
};

interface StaffShiftProcedureContextProps {
  hospitalId: number;
  date: string;
  procedureId: number;
  children: React.ReactNode;
  staffShifts: StaffShiftT[];
}

export const StaffShiftProcedureContext: FC<StaffShiftProcedureContextProps> = ({
  hospitalId,
  date,
  procedureId,
  staffShifts,
  children,
}) => {
  const [doAdd] = useMutation(addStaffShiftProcedure);
  const [doRemove] = useMutation(removeStaffShiftProcedure);

  const add = useCallback(
    async (staffId?: number, hourlyRate?: number) => {
      await doAdd({
        variables: {
          procedureId,
          staffHospitalId: hospitalId,
          staffId,
          hourlyRate,
        },
      });
    },
    [doAdd, hospitalId, procedureId]
  );
  const remove = useCallback(
    async (id: number, shiftHospitalId: number, staffHospitalId: number) => {
      await doRemove({
        variables: {
          procedureId,
          id,
          shiftHospitalId: shiftHospitalId,
          staffHospitalId: staffHospitalId,
        },
      });
    },
    [doRemove, procedureId]
  );

  const staffMembersConfig = useMemo(
    () => ({
      list: listForShift,
      listByPrimarySpecialty: listForShiftByPrimarySpecialty,
      variables: { procedureId, date },
    }),
    [date, procedureId]
  );

  const context = useMemo(
    () => ({
      scope: { procedureId, date },
      staffShifts,
      staffMembers: staffMembersConfig,
      add,
      remove,
    }),
    [add, date, procedureId, remove, staffMembersConfig, staffShifts]
  );

  return (
    <StaffMembersSubscriptionProvider
      staffMembersConfig={staffMembersConfig}
      scopeKey={`procedure-${procedureId}-${date}`}
    >
      <StaffShiftContext.Provider value={context}>{children}</StaffShiftContext.Provider>
    </StaffMembersSubscriptionProvider>
  );
};

interface StaffShiftRoomContextProps {
  hospitalId: number;
  date: string;
  roomId: number;
  staffShifts: StaffShiftT[];
  children: React.ReactNode;
}

export const StaffShiftRoomContext: FC<StaffShiftRoomContextProps> = ({
  hospitalId,
  roomId,
  date,
  staffShifts,
  children,
}) => {
  const [doAdd] = useMutation(addStaffShiftRoom);
  const [doRemove] = useMutation(removeStaffShiftRoom);

  const add = useCallback(
    async (staffId?: number, hourlyRate?: number) => {
      await doAdd({
        variables: {
          roomId,
          date,
          staffHospitalId: hospitalId,
          staffId,
          hourlyRate,
        },
      });
    },
    [date, doAdd, hospitalId, roomId]
  );

  const remove = useCallback(
    async (id: number, shiftHospitalId: number, staffHospitalId: number) => {
      await doRemove({
        variables: {
          roomId,
          date,
          id,
          shiftHospitalId: shiftHospitalId,
          staffHospitalId: staffHospitalId,
        },
      });
    },
    [date, doRemove, roomId]
  );

  const staffMembersConfig = useMemo(
    () => ({
      list: listForShift,
      listByPrimarySpecialty: listForShiftByPrimarySpecialty,
      variables: { roomId, date },
    }),
    [date, roomId]
  );

  const context = useMemo(
    () => ({
      scope: { roomId, date },
      staffShifts,
      staffMembers: staffMembersConfig,
      add,
      remove,
    }),
    [add, date, remove, roomId, staffMembersConfig, staffShifts]
  );

  return (
    <StaffMembersSubscriptionProvider staffMembersConfig={staffMembersConfig} scopeKey={`room-${roomId}-${date}`}>
      <StaffShiftContext.Provider value={context}>{children}</StaffShiftContext.Provider>
    </StaffMembersSubscriptionProvider>
  );
};
